import OliverText from "@builder/components/general/OliverText";
import TextInput from "@builder/components/general/TextInput";
import TooltipElement from "@builder/components/general/Tooltip";
import { getContextDeep } from "@builder/store/slices/BuilderSlice/builderHelpers";
import { getSelectedNode } from "@builder/store/slices/BuilderSlice/builderSelectors";
import { updateDynamicDataType } from "@builder/store/slices/BuilderSlice/builderSlice";
import { closePopup, setActionDynamicData } from "@builder/store/slices/popupSlice";
import { useAppDispatch, useAppSelector } from "@builder/store/store";
import {
    DynamicData,
    DynamicDataGroup,
    DynamicDataGroups,
    DynamicDataKey,
    groupAllowedPerContext,
    isGroupAllowedInContext,
    isSubGroupAllowedInContext
} from "@common/build/dynamicData";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import PopupHeader from "../../PopupHeader";
import PopupSidebar, { SidebarOption } from "../../PopupSidebar";
import "./styles.scss";
import CheckoutFlowData from "@builder/components/app/Popup/popups/DynamicData/CheckoutFlowSettings";

export const DynamicImageElements = ["image", "div", "repeaterDiv"];

const DynamicDataPopup = () => {
    const dispatch = useAppDispatch();
    const selectedNode = useAppSelector(getSelectedNode);
    const { t } = useTranslation();
    const builderState = useAppSelector(state => state.builder);
    const id = useAppSelector(state => state.popup.currentId);
    const type = useAppSelector(state => {
        if (!id || !state.popup.dynamicDataFields[id]) return null;
        return state.popup.dynamicDataFields[id].dynamicDataType;
    });
    const dataContext = type || getContextDeep(builderState, selectedNode?.id) || "general";

    const [searchValue, setSearchValue] = useState<string>("");
    const [currentTabs, setCurrentTabs] = useState<SidebarOption[]>([
        ...DynamicDataGroups.map(group => ({
            value: group,
            label: `${group}`,
            checked: groupAllowedPerContext[dataContext][0]?.group === group,
            disabled: !isGroupAllowedInContext(group, dataContext)
        })),
        {
            value: "themeSetting",
            label: "themeSetting",
            checked: false,
            disabled: false
        }
    ]);

    const searchCallback = (value: string): void => {
        setSearchValue(value);
    };

    const handleTabCallback = (options: SidebarOption[]) => {
        setCurrentTabs([...options]);
    };

    const handleInsertData = (key: DynamicDataKey) => {
        if (type && id) {
            dispatch(setActionDynamicData({ id, data: key }));
        } else {
            dispatch(updateDynamicDataType(key));
        }
        dispatch(closePopup());
    };

    const selectedGroup = currentTabs.find(tab => tab.checked)?.value || ("general" as DynamicDataGroup);
    const isCurrentNodeAnImage = DynamicImageElements.includes(selectedNode?.name || "");

    // Get data items for selected tab
    const currentTabKeys = Object.keys(DynamicData).filter(key => DynamicData[key as DynamicDataKey].group === selectedGroup);
    // Filter by image type and search

    const keysToShow = currentTabKeys
        .filter(key => !DynamicData[key as DynamicDataKey].hide)
        .filter(key => {
            const item = DynamicData[key as DynamicDataKey];
            return isCurrentNodeAnImage ? item.dataType === "image" : item.dataType !== "image";
        })
        .filter(key => {
            const item = DynamicData[key as DynamicDataKey];
            return item?.label?.toLowerCase()?.includes(searchValue?.toLowerCase());
        });

    //group keys by subGroup:
    const groupedData = keysToShow.reduce((acc, key) => {
        const item = DynamicData[key as DynamicDataKey];
        const subGroupName = item.subGroup;
        if (!isSubGroupAllowedInContext(selectedGroup as DynamicDataGroup, subGroupName, dataContext)) {
            return acc;
        }

        if (!acc[subGroupName]) {
            acc[subGroupName] = [];
        }
        acc[subGroupName].push(key as DynamicDataKey);
        return acc;
    }, {} as Record<string, DynamicDataKey[]>);

    const groupedDataArray = Object.entries(groupedData);

    return (
        <div className="dynamic-data-wrapper">
            <div className="header-wrapper">
                <PopupHeader src="./db-icon.svg" title="Insert Dynamic data">
                    <TextInput value={searchValue} placeholder="Search" callback={searchCallback} className="d-data-search" />
                </PopupHeader>
            </div>

            <div className="dynamic-data-body">
                <div className="dd-left-col">
                    <PopupSidebar options={currentTabs} callback={handleTabCallback} style={{ padding: "0 0 14px 0" }} />
                </div>
                <div className="dd-right-col">
                    {currentTabs.find(tab => tab.value === "themeSetting")?.checked ? (
                        <CheckoutFlowData type={isCurrentNodeAnImage ? "image" : "text"} />
                    ) : (
                        <>
                            <div className="dynamic-data-explain">
                                <OliverText color="base-grey" text={t("dynamicData.explain")} />
                                <TooltipElement text={t("dynamicData.tooltip")}>
                                    <img src="./circle-question.svg" alt="" />
                                </TooltipElement>
                            </div>
                            <div className="dynamic-data-list">
                                {groupedDataArray.length ? (
                                    groupedDataArray.map(([groupName, keys]) => (
                                        <div className="dd-group" key={groupName}>
                                            {keys.length > 0 && (
                                                <div className="dd-list-title">
                                                    <OliverText text={t(groupName)} color="base-grey" size="xs" weight="light" />
                                                </div>
                                            )}
                                            {keys.map(key => (
                                                <button
                                                    key={key}
                                                    className="dynamic-data-item"
                                                    onClick={() => handleInsertData(key as DynamicDataKey)}
                                                >
                                                    <div className="dd-item-row">
                                                        <img src="./final-logo.svg" alt="" />
                                                        <OliverText
                                                            text={t(DynamicData[key as DynamicDataKey].label)}
                                                            size="s"
                                                            weight="semi-bold"
                                                            translatePrefix=""
                                                        />
                                                    </div>
                                                    <OliverText
                                                        text={t(DynamicData[key as DynamicDataKey].info)}
                                                        size="xs"
                                                        color="base-grey"
                                                        translatePrefix=""
                                                        style={{ textAlign: "left" }}
                                                    />
                                                </button>
                                            ))}
                                        </div>
                                    ))
                                ) : (
                                    <div className="dd-group">
                                        <OliverText
                                            text={`${t("dynamicData.There is no dynamic data available for this node in")} ${t(
                                                `popup.popupSidebar.dynamicData.${dataContext}`
                                            )} ${t("dynamicData.context")}.`}
                                            color="base-grey"
                                            size="s"
                                            weight="light"
                                        />
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default DynamicDataPopup;
